/**
 * AIProvidersList
 *
 * Renders cards for all 3 AI providers.
 *
 * @module ai-settings/components
 */

import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { AIProviderCard } from './AIProviderCard';
import { AIKeyDialog } from './AIKeyDialog';
import type { AIProviderType, AIProvidersStatus, AIProviderInfo } from '../types/aiKey.types';
import { AI_PROVIDERS } from '../types/aiKey.types';

interface AIProvidersListProps {
  providers: AIProvidersStatus;
  onStoreKey: (provider: AIProviderType, apiKey: string) => Promise<void>;
  onDeleteKey: (provider: AIProviderType) => Promise<void>;
  onValidateKey: (provider: AIProviderType, apiKey: string) => Promise<boolean>;
  saving: boolean;
  validating: boolean;
  disabled?: boolean;
}

export const AIProvidersList: React.FC<AIProvidersListProps> = ({
  providers,
  onStoreKey,
  onDeleteKey,
  onValidateKey,
  saving,
  validating,
  disabled,
}) => {
  const [dialogProvider, setDialogProvider] = useState<AIProviderInfo | null>(null);

  const handleAdd = useCallback((type: AIProviderType) => {
    const info = AI_PROVIDERS.find(p => p.type === type);
    if (info) setDialogProvider(info);
  }, []);

  const handleEdit = useCallback((type: AIProviderType) => {
    const info = AI_PROVIDERS.find(p => p.type === type);
    if (info) setDialogProvider(info);
  }, []);

  const handleDelete = useCallback(async (type: AIProviderType) => {
    if (window.confirm(`Remove ${AI_PROVIDERS.find(p => p.type === type)?.name} API key?`)) {
      await onDeleteKey(type);
    }
  }, [onDeleteKey]);

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        API Keys
      </Typography>

      {AI_PROVIDERS.map((provider) => (
        <AIProviderCard
          key={provider.type}
          provider={provider}
          metadata={providers[provider.type] || { configured: false }}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          disabled={disabled || saving}
        />
      ))}

      {dialogProvider && (
        <AIKeyDialog
          open={!!dialogProvider}
          onClose={() => setDialogProvider(null)}
          provider={dialogProvider}
          onSave={onStoreKey}
          onValidate={onValidateKey}
          saving={saving}
          validating={validating}
        />
      )}
    </Box>
  );
};
